import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Brain, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage, formatDate } from "@/lib/api";
import { startGeneral, startPersonal } from "@/lib/quiz";
import { PageHeader } from "@/components/PageHeader";
import { CountList, Empty, Fold, InsightCard, LockedSections, ProgressCard, Stat, WeeklyBars } from "@/components/AnalyticsCards";
import { FocusCard, GameList, ProgressBlock } from "@/components/AnalyticsExtra";
import { FreeReport, ReportPanel } from "@/components/GameReport";

const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;

function Overview({ b }) {
  return (
    <div className="space-y-2.5" data-testid="analytics-overview">
      <div className="grid grid-cols-3 gap-2">
        <Stat label="Sessions" value={b.sessions} sub={b.first_date ? `since ${formatDate(b.first_date)}` : "none yet"} testid="analytics-sessions" />
        <Stat label="Per week" value={b.per_week_8w} sub="avg, 8 weeks" testid="analytics-per-week" />
        <Stat label="Last 30 days" value={b.last_30_days} sub="sessions" testid="analytics-30d" />
        <Stat label="Days" value={b.training_days} sub="training days" testid="analytics-days" />
        <Stat label="Consistency" value={<>{b.weeks_trained_8w}<span className="text-base text-[#71717A]">/8</span></>} sub="weeks trained" testid="analytics-consistency" />
        <Stat label="Week streak" value={b.week_streak} sub={b.week_streak === 1 ? "week in a row" : "weeks in a row"} testid="analytics-week-streak" />
      </div>
      <WeeklyBars weeks={b.weekly} />
    </div>
  );
}

function Logged({ b }) {
  return (
    <Fold title="Positions & techniques" summary={b.top_positions[0]?.name || b.top_techniques[0]?.name || "None yet"} testid="analytics-positions">
      <div className="eyebrow px-1 pt-1">Most-logged positions</div>
      <CountList items={b.top_positions} testid="analytics-position-list" empty="No positions logged yet." />
      <div className="eyebrow px-1 pt-1">Most-logged techniques</div>
      <CountList items={b.top_techniques} testid="analytics-technique-list" empty="No techniques logged yet." />
    </Fold>
  );
}

function QuizWeak({ quiz, onRevise, busy }) {
  if (quiz.answered < 4) return <Empty testid="analytics-quiz-empty">Answer a few more quiz questions ({quiz.answered} so far) to see which subjects need revision.</Empty>;
  if (!quiz.weak.length) return <Empty testid="analytics-quiz-empty">Not enough answers per topic yet. Each topic needs at least 4 answers.</Empty>;
  const note = <p key="note" className="text-[11px] text-[#71717A]">Quiz results show technical knowledge, not how you roll. Small samples are an early signal, not a verdict.</p>;
  return [note, ...quiz.weak.map((w) => (
    <div key={w.topic} className="card p-4" data-testid={`analytics-weak-${w.topic}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="font-semibold">{w.label}</div>
        <span className={`text-sm font-bold ${w.accuracy < 60 ? "text-[#E50914]" : "text-[#F59E0B]"}`}>{w.accuracy}%</span>
      </div>
      <div className="text-[11px] text-[#71717A] mt-0.5">{w.right} of {w.total} answers correct · {w.confidence}{w.trend ? ` · earlier ${w.trend.earlier}% → recent ${w.trend.recent}%` : ""}</div>
      {w.missed_examples.length > 0 && <ul className="mt-2 space-y-1 text-[12.5px] text-[#A1A1AA] list-disc pl-4">{w.missed_examples.map((q) => <li key={q}>{q}</li>)}</ul>}
      <button onClick={() => onRevise(w.topic)} disabled={!!busy} className="btn-secondary w-full mt-3 h-10 text-sm" data-testid={`analytics-revise-${w.topic}`}>
        {busy === w.topic ? <Loader2 size={15} className="animate-spin" /> : `Revise ${w.label} (missed questions first)`}
      </button>
    </div>
  ))];
}

function Progress({ a, ins }) {
  const extra = ProgressBlock({ a });
  const ai = a.organised_sessions >= a.min_progress_sessions ? ins.progress.map((p, i) => <ProgressCard key={i} item={p} />) : [];
  const n = extra.length + ai.length;
  const short = a.min_progress_sessions - a.organised_sessions;
  return (
    <Fold title="Progress over time" summary={n ? plural(n, "change") : "None yet"} testid="analytics-progress">
      {n ? [...ai, ...extra] : <Empty>{short > 0 ? `Log ${short} more session${short === 1 ? "" : "s"} to compare earlier and recent training. Write what went well and what didn't.` : "No clear change between your earlier and recent sessions yet."}</Empty>}
    </Fold>
  );
}

function Advanced({ a, onRevise, busy, onPersonal, active, onSaved }) {
  const ins = a.insights;
  return (
    <>
      {!ins ? null : (
        <>
          <Fold title="What to focus on next" summary={ins.focus.length === 1 ? "1 priority" : `${ins.focus.length} priorities`} testid="analytics-focus">
            {ins.focus.length ? ins.focus.map((f, i) => <FocusCard key={f.title} item={f} index={i} active={active} onSaved={onSaved} />)
              : <Empty>No clear priority yet. Describe what went wrong and why in your next logs.</Empty>}
            <button onClick={onPersonal} disabled={!!busy} className="btn-primary w-full" data-testid="analytics-personal-quiz">
              {busy === "personal" ? <Loader2 size={16} className="animate-spin" /> : <><Brain size={16} /> Quiz me on what I'm training</>}
            </button>
          </Fold>
          <Fold title="What's working" summary={ins.working.length ? plural(ins.working.length, "strength") : "None yet"} testid="analytics-working">
            <p className="text-[11px] text-[#71717A] px-1">Self-reported in your logs.</p>
            {ins.working.length ? ins.working.map((w, i) => <InsightCard key={i} item={w} tone="good" badge={`Reported ${w.sessions}×`} testid={`analytics-working-${i}`} />) : <Empty>No repeated successes logged yet.</Empty>}
          </Fold>
          <Fold title="Recurring problems" summary={ins.problems.length ? plural(ins.problems.length, "problem") : "None found"} testid="analytics-problems">
            {ins.problems.length ? ins.problems.map((p, i) => <InsightCard key={i} item={p} tone="bad" badge={`${p.sessions} session${p.sessions === 1 ? "" : "s"}`} testid={`analytics-problem-${i}`} />) : <Empty>No recurring problems found in your logs.</Empty>}
          </Fold>
          <Progress a={a} ins={ins} />
        </>
      )}
      <Fold title="Your BJJ game" summary={a.game.length ? `${a.game.length} tracked` : "None yet"} testid="analytics-game-section"><GameList game={a.game} /></Fold>
      <Fold title="Quiz weak spots" summary={a.quiz.weak.length ? plural(a.quiz.weak.length, "topic") : `${a.quiz.answered} answered`} testid="analytics-quiz"><QuizWeak quiz={a.quiz} onRevise={onRevise} busy={busy} /></Fold>
      {a.generated_at && <p className="text-[11px] text-[#52525B] text-center pb-2">Game Report generated {formatDate(a.generated_at.slice(0, 10))}</p>}
    </>
  );
}

export default function Analytics() {
  const nav = useNavigate();
  const [busy, setBusy] = useState(null);
  const { data: focus, refetch: refetchFocus } = useQuery({ queryKey: ["focus"], queryFn: () => api.get("/focus").then((r) => r.data) });
  const { data, isLoading } = useQuery({
    queryKey: ["analytics"],
    queryFn: () => api.get("/analytics").then((r) => r.data),
    refetchOnMount: "always",
    refetchInterval: (q) => (q.state.data?.report?.status === "generating" ? 4000 : false),
  });
  const run = (key, fn) => async () => {
    setBusy(key);
    try {
      const a = await fn();
      nav(`/quiz/${a.id}`);
    } catch (err) {
      toast.error(errorMessage(err));
      setBusy(null);
    }
  };
  return (
    <div data-testid="analytics-page">
      <PageHeader eyebrow="Your training" title="Analytics" />
      <div className="px-5 space-y-3 pb-4">
      {isLoading || !data ? <div className="card h-64 animate-pulse" /> : (
        <>
          <Overview b={data.basic} />
          <Logged b={data.basic} />
          {data.basic.plain_notes > 0 && <p className="text-[11px] text-[#71717A]" data-testid="analytics-plain-note">{data.basic.plain_notes} plain-note entr{data.basic.plain_notes === 1 ? "y isn't" : "ies aren't"} AI-organised, so {data.basic.plain_notes === 1 ? "it isn't" : "they aren't"} included in the analysis.</p>}
          <ReportPanel r={data.report} />
          {data.pro ? <><Advanced a={data.advanced} busy={busy} onRevise={(t) => run(t, () => startGeneral(t, "all", 10, true))()} onPersonal={run("personal", startPersonal)} active={focus?.active || []} onSaved={refetchFocus} /></> : <><FreeReport r={data.report} /><LockedSections /></>}
        </>
      )}
      </div>
    </div>
  );
}
